import { z } from 'zod';
import { ChannelKindSchema, WEEKDAYS } from './enums.js';

// AI operating schedule (PRD §13). Times are local wall-clock "HH:MM" in the schedule's IANA timezone.
// An interval whose end is earlier than (or equal to) its start is an explicit overnight interval that
// continues into the next day. "24:00" is allowed as an end-of-day marker.

const Hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$|^24:00$/, 'Formato HH:MM');

export const TimeIntervalSchema = z
  .object({ start: Hhmm, end: Hhmm })
  .refine((i) => i.start !== '24:00', { message: 'El inicio no puede ser 24:00', path: ['start'] });
export type TimeInterval = z.infer<typeof TimeIntervalSchema>;

export const WeeklyHoursSchema = z.object(
  Object.fromEntries(WEEKDAYS.map((d) => [d, z.array(TimeIntervalSchema).max(6)])) as Record<
    (typeof WEEKDAYS)[number],
    z.ZodArray<typeof TimeIntervalSchema>
  >,
);
export type WeeklyHours = z.infer<typeof WeeklyHoursSchema>;

export const ScheduleExceptionSchema = z.object({
  date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  closed: z.boolean(),
  intervals: z.array(TimeIntervalSchema).max(6).default([]),
  label: z.string().max(120).nullable().optional(),
});
export type ScheduleExceptionInput = z.infer<typeof ScheduleExceptionSchema>;

export const OperatingScheduleInputSchema = z.object({
  projectId: z.string().nullable().default(null),
  channel: ChannelKindSchema.nullable().default(null),
  timezone: z.string().min(1).max(64),
  alwaysOpen: z.boolean().default(false),
  weekly: WeeklyHoursSchema,
  exceptions: z.array(ScheduleExceptionSchema).max(366).default([]),
  outOfHoursAck: z.boolean().default(false),
  outOfHoursAckText: z.string().max(600).nullable().optional(),
});
export type OperatingScheduleInput = z.infer<typeof OperatingScheduleInputSchema>;

export const OperatingScheduleSchema = OperatingScheduleInputSchema.extend({
  id: z.string(),
  version: z.number().int(),
  updatedAt: z.string(),
});
export type OperatingSchedule = z.infer<typeof OperatingScheduleSchema>;

export const ScheduleStatusSchema = z.object({
  /** Which saved schedule applies (null = none configured → AI does not operate). */
  effectiveScheduleId: z.string().nullable(),
  source: z.enum(['project_channel', 'project', 'workspace_channel', 'workspace', 'none']),
  timezone: z.string().nullable(),
  openNow: z.boolean(),
  alwaysOpen: z.boolean(),
  /** ISO timestamps (UTC) */
  nextOpenAt: z.string().nullable(),
  nextCloseAt: z.string().nullable(),
  /** Next 7 local days of computed open windows, for preview. */
  week: z.array(z.object({ date: z.string(), weekday: z.string(), windows: z.array(z.object({ start: z.string(), end: z.string() })), exception: z.string().nullable() })),
});
export type ScheduleStatus = z.infer<typeof ScheduleStatusSchema>;

/** Editable preset — offered, never assumed (PRD §13). */
export const WEEKDAY_9_TO_7_PRESET: WeeklyHours = {
  mon: [{ start: '09:00', end: '19:00' }],
  tue: [{ start: '09:00', end: '19:00' }],
  wed: [{ start: '09:00', end: '19:00' }],
  thu: [{ start: '09:00', end: '19:00' }],
  fri: [{ start: '09:00', end: '19:00' }],
  sat: [],
  sun: [],
};

export const EMPTY_WEEK: WeeklyHours = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };

// ───────────── Proactive contact window (setter spec §02, §06.8, §10.6, §15.4) ─────────────
// Every day, local wall-clock: AI-originated calls, unsolicited WhatsApp, campaigns and cadence steps run only inside
// it (project timezone, and the prospect's too when known). Outside it is «horario nocturno»: WhatsApp still answers
// the prospect reactively, identifying itself as an AI assistant, unless the project opts out of night replies.
// The window is a per-workspace setting with an optional per-project override (default 08:00–21:00).

export const DEFAULT_CONTACT_WINDOW = { start: '08:00', end: '21:00' } as const;

export const ContactWindowSchema = z
  .object({ start: Hhmm, end: Hhmm })
  .refine((w) => w.start !== '24:00' && w.end !== '00:00' && w.start < w.end, { message: 'El inicio debe ser anterior al fin (mismo día)', path: ['end'] });
export type ContactWindow = z.infer<typeof ContactWindowSchema>;

export const HhmmSchema = Hhmm;
